import { Button } from "@admin/components/atoms/Button";
import { ErrorMessage } from "@admin/components/atoms/ErrorMessage";
import { Input } from "@admin/components/atoms/Input";
import { Label } from "@admin/components/atoms/Input/Label";
import { Modal, ModalProps } from "@admin/components/atoms/Modal";
import { updateCompanyName } from "@enos-monorepo/lib";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { z } from "zod";

interface EditCompanyNameModalProps extends ModalProps {
  companyId?: string;
  companyName?: string;
  handleSuccessModalAction: () => void;
}

export function EditCompanyNameModal({
  companyId,
  companyName,
  handleSuccessModalAction,
  ...props
}: EditCompanyNameModalProps) {
  const { t } = useTranslation();
  const { mutate, isPending } = useMutation({ mutationFn: updateCompanyName });

  const companyNameSchema = z.object({
    companyName: z
      .string()
      .min(2, {
        message: t("settings.Company name must be at least 2 characters"),
      })
      .max(50, {
        message: t("settings.Company name must be at most 50 characters"),
      }),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: zodResolver(companyNameSchema),
  });

  const handleFormSubmit = handleSubmit((data) => {
    if (companyId) {
      mutate(
        { companyId, companyName: data.companyName },
        {
          onSuccess: () => {
            props.onClose?.();
            toast.success(t("common.The changes have been saved"));
            reset();
            handleSuccessModalAction();
          },
          onError: (err: Error) => {
            toast.error(t("common.Unable to save the changes") + err.message);
          },
        },
      );
    }
  });

  const handleDiscard = () => {
    props.onClose?.();
    reset();
  };

  return (
    <Modal
      {...props}
      title={t("settings.Edit Company Name")}
      actionButtons={
        <>
          <Button variant="secondary" onClick={handleDiscard}>
            {t("common.Discard")}
          </Button>
          <Button
            variant="primary"
            type="submit"
            loading={isPending}
            onClick={handleFormSubmit}
          >
            {t("common.Save Changes")}
          </Button>
        </>
      }
    >
      <Label title={t("settings.Company Name")} required>
        <form onSubmit={handleFormSubmit} className="w-full">
          <Input
            size="sm"
            type="text"
            placeholder={t("settings.Edit Company Name")}
            defaultValue={companyName}
            {...register("companyName")}
          />
          <ErrorMessage message={errors.companyName?.message as string} />
        </form>
      </Label>
    </Modal>
  );
}
